import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

const noop = () => {};

export default function ImageGalleryOverlay({
  isOpen = false,
  items = [],
  initialIndex = 0,
  heading = "Image gallery",
  eyebrow = "",
  slimModal = false,
  onClose = noop,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const closeRef = useRef(null);
  const itemCount = items.length;
  const currentItem = items[currentIndex] || null;

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(
        Math.max(0, Math.min(itemCount - 1, Number(initialIndex) || 0))
      );
    }
  }, [initialIndex, isOpen, itemCount]);

  useEffect(() => {
    if (!isOpen || itemCount < 2) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setCurrentIndex((index) => Math.max(0, index - 1));
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setCurrentIndex((index) => Math.min(itemCount - 1, index + 1));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, itemCount]);

  const goPrevious = () => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  };

  const goNext = () => {
    setCurrentIndex((index) => Math.min(itemCount - 1, index + 1));
  };

  return (
    <Transition show={isOpen && Boolean(currentItem)} as={Fragment} appear>
      <Dialog
        as="div"
        className="b-image-gallery__overlay"
        initialFocus={closeRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-smooth duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-smooth duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="b-image-gallery__overlay-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="b-image-gallery__overlay-wrapper">
          <Transition.Child
            as={Fragment}
            enter="ease-smooth duration-500"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-smooth duration-500"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="b-image-gallery__overlay-panel">
              <button
                type="button"
                className="b-image-gallery__overlay-close"
                onClick={onClose}
                aria-label="Close image gallery"
                ref={closeRef}
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="15"
                  fill="none"
                  viewBox="0 0 16 15"
                >
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    d="m1.119.707 13.435 13.435M14.142.707.707 14.142"
                  />
                </svg>
              </button>

              <div
                className={`b-image-gallery__overlay-body${
                  slimModal ? " is-slim" : ""
                }`}
              >
                {slimModal ? (
                  <Dialog.Title className="sr-only">{heading}</Dialog.Title>
                ) : (
                  <div className="b-image-gallery__overlay-heading">
                    {eyebrow ? (
                      <div className="c-section-heading__eyebrow">
                        {eyebrow}
                      </div>
                    ) : null}
                    <Dialog.Title className="c-section-heading__heading">
                      {heading}
                    </Dialog.Title>
                  </div>
                )}

                <div className="b-image-gallery__overlay-media">
                  {itemCount > 1 ? (
                    <button
                      type="button"
                      className="b-image-gallery__overlay-control b-image-gallery__overlay-control--previous"
                      onClick={goPrevious}
                      disabled={currentIndex === 0}
                      aria-label="Previous image"
                    >
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="19" fill="none" viewBox="0 0 20 19">
                        <path stroke="currentColor" strokeWidth="1.5" d="M19.451 9.225H1.571M10.09.362.724 9.302l9.366 8.94"/>
                      </svg>
                    </button>
                  ) : null}

                  <div className="b-image-gallery__overlay-image-stage">
                    {items.map((item, index) => {
                      const isSelected = index === currentIndex;

                      return (
                        <div
                          className={`b-image-gallery__overlay-image${
                            isSelected ? " is-selected" : ""
                          }`}
                          role={isSelected ? "group" : undefined}
                          aria-roledescription={
                            isSelected ? "slide" : undefined
                          }
                          aria-label={
                            isSelected
                              ? `Image ${index + 1} of ${itemCount}${
                                  item.caption ? `: ${item.caption}` : ""
                                }`
                              : undefined
                          }
                          aria-hidden={isSelected ? undefined : "true"}
                          dangerouslySetInnerHTML={{ __html: item.imageHTML }}
                          key={item.id}
                        />
                      );
                    })}
                  </div>

                  {itemCount > 1 ? (
                    <button
                      type="button"
                      className="b-image-gallery__overlay-control b-image-gallery__overlay-control--next"
                      onClick={goNext}
                      disabled={currentIndex === itemCount - 1}
                      aria-label="Next image"
                    >
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="19" fill="none" viewBox="0 0 20 19">
                        <path stroke="currentColor" strokeWidth="1.5" d="M0 9.156h17.88M9.361.542l9.366 8.94-9.366 8.94"/>
                      </svg>
                    </button>
                  ) : null}
                </div>

                {currentItem.caption ? (
                  <p className="b-image-gallery__overlay-caption">
                    {currentItem.caption}
                  </p>
                ) : null}

                {itemCount > 1 ? (
                  <div
                    className="b-image-gallery__overlay-pagination"
                    aria-label="Choose image"
                  >
                    {items.map((item, index) => (
                      <button
                        type="button"
                        className="b-image-gallery__overlay-dot"
                        aria-label={`Go to image ${index + 1} of ${itemCount}`}
                        aria-current={index === currentIndex ? "true" : undefined}
                        onClick={() => setCurrentIndex(index)}
                        key={item.id}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
